import { useMemo } from 'react';

import { Flex, HStack, Text } from '@chakra-ui/react';

import { wordPluralisation } from '@nocowanie/common-ui/helpers';

import { AccommodationTileRoomInfoProps } from '@app/components/molecules/accommodation-tile';
import { APARTMENT_TYPE_LABEL } from '@app/consts';

export const AccommodationTileRoomInfo = ({
    apartment,
    t,
    ...rest
}: AccommodationTileRoomInfoProps) => {
    const { type, singleBeds, doubleBeds, size, defaultGuests } = apartment;

    const singleBedsCount = +(singleBeds ?? 0);
    const doubleBedsCount = +(doubleBeds ?? 0);
    const bedsCount = singleBedsCount + doubleBedsCount;

    const translationBedsValue = t('roomInfo.bed', {
        returnObjects: true,
    }) as Record<string, string>;
    const translationSingleBedsValue = t('roomInfo.single', {
        returnObjects: true,
    }) as Record<string, string>;
    const translationDoubleBedsValue = t('roomInfo.double', {
        returnObjects: true,
    }) as Record<string, string>;

    const singleBedsLabel = wordPluralisation(
        singleBedsCount,
        translationSingleBedsValue.singular,
        translationSingleBedsValue.plural,
        translationSingleBedsValue.genitivePlural,
    );

    const doubleBedsLabel = wordPluralisation(
        doubleBedsCount,
        translationDoubleBedsValue.singular,
        translationDoubleBedsValue.plural,
        translationDoubleBedsValue.genitivePlural,
    );

    const bedBreakdown = useMemo(() => {
        const hasBothTypes = singleBedsCount > 0 && doubleBedsCount > 0;

        if (hasBothTypes) {
            const parts: string[] = [];
            parts.push(`${singleBedsCount} ${singleBedsLabel}`);
            parts.push(`${doubleBedsCount} ${doubleBedsLabel}`);
            return `(${parts.join(', ')})`;
        }

        if (singleBedsCount > 0) {
            return singleBedsLabel;
        }

        if (doubleBedsCount > 0) {
            return doubleBedsLabel;
        }

        return '';
    }, [singleBedsCount, doubleBedsCount, singleBedsLabel, doubleBedsLabel]);

    const guestCapacityLabel = useMemo(() => {
        if (!defaultGuests || !type) {
            return null;
        }

        return ` ${defaultGuests}-${APARTMENT_TYPE_LABEL[type].guestCapacityLabel}`;
    }, [defaultGuests, type]);

    return (
        <Flex
            direction={{ base: 'row', md: 'column' }}
            fontSize={'xs'}
            flexWrap={'wrap'}
            columnGap={0.5}
            {...rest}
        >
            {type ? (
                <>
                    <Text as={'span'} fontWeight={'bold'} hideBelow={'md'}>
                        {APARTMENT_TYPE_LABEL[type].label}
                        {guestCapacityLabel}
                    </Text>
                    <Text as={'span'} fontWeight={'bold'} hideFrom={'md'}>
                        {APARTMENT_TYPE_LABEL[type].label}
                        {size || bedsCount ? ':' : null}
                    </Text>
                </>
            ) : null}
            <HStack columnGap={1.5} flexWrap={'wrap'}>
                {size ? (
                    <Text
                        as={'span'}
                        css={
                            bedsCount && {
                                _after: {
                                    content: '"\u2022"',
                                    mt: '-1px',
                                    ml: 1.5,
                                    transform: 'scale(1.25)',
                                },
                            }
                        }
                    >
                        {size} m<sup>2</sup>
                    </Text>
                ) : null}
                {bedsCount ? (
                    <Text as={'span'}>
                        {bedsCount}{' '}
                        {wordPluralisation(
                            bedsCount,
                            translationBedsValue.singular,
                            translationBedsValue.plural,
                            translationBedsValue.genitivePlural,
                        )}
                        <Text as={'span'} hideBelow={'md'}>
                            {' '}
                            {bedBreakdown}
                        </Text>
                    </Text>
                ) : null}
            </HStack>
        </Flex>
    );
};
